// src/components/homepage/HeroBanner.js
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import Button from '../common/Button';
import { useContent } from '../../context/ContentContext';
import SectionLoader from '../common/SectionLoader';

function HeroBanner() {
  const { content, loading, error } = useContent();
  const [slides, setSlides] = useState([]);
  const [currentImage, setCurrentImage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const imageRef = useRef(null);
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const btnRef = useRef(null);
  const intervalRef = useRef(null);

  // GSAP entrance animations on slide change
  useEffect(() => {
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

    tl.fromTo(imageRef.current, { scale: 1.1, opacity: 0 }, { scale: 1, opacity: 1, duration: 1.4 })
      .fromTo(titleRef.current, { y: 60, opacity: 0 }, { y: 0, opacity: 1, duration: 1 }, "-=0.8")
      .fromTo(descRef.current, { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9 }, "-=0.7")
      .fromTo(btnRef.current, { scale: 0.8, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.7 }, "-=0.6");

    // Subtle continuous breathing effect on image
    gsap.to(imageRef.current, {
      scale: 1.03,
      duration: 8,
      ease: "sine.inOut",
      repeat: -1,
      yoyo: true,
    });
  }, [currentImage]);

  // Set slides from content context
  useEffect(() => {
    if (loading) return;
    if (error || !content.heroBanner) {
      // Fallback content
      setSlides([
        {
          id: 1,
          image: '/assets/images/hero/fallback.jpg',
          title: 'Welcome',
          description: 'Explore what we have for you.',
          buttonText: 'Discover',
          buttonLink: '/shop',
        },
      ]);
    } else {
      setSlides(content.heroBanner);
    }
  }, [content, loading, error]);

  const switchImage = (index) => {
    if (index === currentImage || slides.length === 0) return;
    setCurrentImage(index);
  };

  // Auto-advance carousel
  useEffect(() => {
    if (!isPaused && slides.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentImage((prev) => (prev + 1) % slides.length);
      }, 6000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPaused, slides.length]);

  // Preload next image
  useEffect(() => {
    if (slides.length === 0) return;
    const nextIndex = (currentImage + 1) % slides.length;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = slides[nextIndex].image;
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, [currentImage, slides]);

  // Pause/resume on hover/touch
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  // Keyboard navigation for bullets
  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      switchImage(index);
    }
  };

  // Navigate left/right
  const goPrev = () => setCurrentImage((prev) => (prev - 1 + slides.length) % slides.length);
  const goNext = () => setCurrentImage((prev) => (prev + 1) % slides.length);

  // Loading state – now using SectionLoader
  if (loading) {
    return <SectionLoader message="Preparing the banner..." />;
  }

  // Error or no content
  if (error || slides.length === 0) {
    return (
      <div className="relative h-[50vh] w-full flex items-center justify-center bg-gray-100">
        <p className="text-red-500 text-lg font-montserrat">
          {error || 'No banner content available.'}
        </p>
      </div>
    );
  }

  return (
    <div
      className="relative h-[45vh] sm:h-[50vh] md:h-[60vh] lg:h-[65vh] w-full flex items-center justify-center overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleMouseEnter}
      onTouchEnd={handleMouseLeave}
    >
      <img
        ref={imageRef}
        src={`${process.env.REACT_APP_STORE_URL}/hero_banners/${slides[currentImage].image}`}
        alt={`${slides[currentImage].title} slide ${currentImage + 1}`}
        className="absolute inset-0 w-full h-full object-cover z-0 transition-opacity duration-700 ease-in-out"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent z-10" />

      {/* Stronger gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/45 to-black/75 z-10" />

      <div className="relative z-20 text-center px-5 sm:px-8 md:px-12 max-w-4xl space-y-5 sm:space-y-7 md:space-y-8">
        <h1
          ref={titleRef}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-playfair-display font-bold text-cream drop-shadow-2xl leading-tight"
        >
          {slides[currentImage].title}
        </h1>
        <p
          ref={descRef}
          className="text-base sm:text-lg md:text-xl lg:text-2xl text-cream font-montserrat drop-shadow-lg max-w-2xl mx-auto"
        >
          {slides[currentImage].description}
        </p>
        <div ref={btnRef}>
          <Link to={slides[currentImage].buttonLink}>
            <Button
              className="bg-primary text-cream px-7 py-3 sm:px-8 sm:py-4 text-base sm:text-lg md:text-xl font-montserrat font-semibold rounded-full shadow-2xl hover:bg-primary/90 transition transform hover:scale-105 active:scale-95"
              aria-label={`Shop ${slides[currentImage].title}`}
            >
              {slides[currentImage].buttonText}
            </Button>
          </Link>
        </div>
      </div>

      {/* Arrow Navigation */}
      <button
        onClick={goPrev}
        className="absolute left-3 sm:left-6 top-1/2 transform -translate-y-1/2 bg-black/50 text-cream p-2.5 sm:p-3 rounded-full hover:bg-primary/80 transition z-30"
        aria-label="Previous slide"
      >
        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={goNext}
        className="absolute right-3 sm:right-6 top-1/2 transform -translate-y-1/2 bg-black/50 text-cream p-2.5 sm:p-3 rounded-full hover:bg-primary/80 transition z-30"
        aria-label="Next slide"
      >
        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Bullet Navigation */}
      <div className="absolute bottom-6 sm:bottom-8 flex gap-2.5 sm:gap-3 z-30">
        {slides.map((slide, idx) => (
          <button
            key={slide.id}
            onClick={() => switchImage(idx)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                switchImage(idx);
              }
            }}
            className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
              currentImage === idx ? 'bg-cream scale-125' : 'bg-cream/50 hover:bg-cream/80'
            }`}
            aria-label={`Go to slide ${idx + 1}`}
            tabIndex={0}
          />
        ))}
      </div>
    </div>
  );
}

export default HeroBanner;